import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { HotkeysDirective } from "@ngneat/hotkeys";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MessageService } from "primeng/api";
import { filter } from "rxjs";

import { AuthService } from "@/app/core/services/auth.service";
import { LayoutStore } from "@/app/core/state/layout.store";
import { ShortcutComponent } from "@/app/shared/components/shortcut/shortcut.component";
import { DRAWER_LINKS } from "@/app/shared/constants";
import { environment } from "@/environments/environment";
import { Theme } from "../models";
import { UserMenuComponent } from "./user-menu/user-menu.component";

/**
 * Drawer navigation component that provides application navigation links,
 * favorites management, and responsive mobile behavior.
 */
@Component({
  selector: "app-drawer",
  imports: [
    HotkeysDirective,
    RouterModule,
    CommonModule,
    ShortcutComponent,
    TranslateModule,
    UserMenuComponent,
  ],
  templateUrl: "drawer.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawerComponent {
  private router = inject(Router);
  private toastService = inject(MessageService);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);
  readonly layoutStore = inject(LayoutStore);
  readonly links = inject(DRAWER_LINKS);

  isDrawerOpen = input<boolean>(false);
  isMobile = input<boolean>(false);
  toggleDrawer = output<void>();
  logoBase = environment.logoBase;

  Theme = Theme;

  private readonly MAX_FAV = 10;
  private readonly destroyRef = inject(DestroyRef);
  readonly currentUrl = signal(this.router.url.split("?")[0]);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects.split("?")[0]);
        this.expandActiveSubmenu();
      });

    this.expandActiveSubmenu();
  }

  /**
   * Checks if the given URL matches the current active route.
   *
   * @param {string | undefined} url The URL to check.
   * @param {boolean} exact Whether to require an exact match.
   *
   * @returns {boolean} True if the URL is active.
   */
  isActive(url: string | undefined, exact = false): boolean {
    if (url === undefined) return false;
    const current = this.currentUrl();
    const normalized = url === "" ? "/" : `/${url}`;
    if (normalized === "/" || exact) return current === normalized;
    return current === normalized || current.startsWith(`${normalized}/`);
  }

  /**
   * Toggles the collapsed state of a navigation link group.
   *
   * @param {string} linkTitle - The title of the link to toggle
   */
  toggleIsCollapsed(linkTitle: string): void {
    const link = this.links.find((l) => l.title === linkTitle);
    if (link) {
      link.isCollapsed = !link.isCollapsed;
    }
  }

  /**
   * Closes the drawer when in mobile view.
   */
  closeDrawerOnMobile(): void {
    if (this.isMobile()) {
      this.toggleDrawer.emit();
    }
  }

  /**
   * Checks if a URL is marked as favorite.
   *
   * @param {string} url - The URL to check
   * @returns {boolean} True if the URL is in favorites
   */
  isFavItem(url: string): boolean {
    return this.layoutStore.favUrls().some((item) => item === url);
  }

  /**
   * Retrieves the display title for a given URL from navigation links.
   *
   * @param {string} url - The URL to find the title for
   * @returns {string} The title of the link or empty string if not found
   */
  getLinkTitle(url: string): string {
    const mainLink = this.links.find((link) => link.url === url);

    if (mainLink) {
      return mainLink.title;
    }

    const subItem = this.links.flatMap((link) => link.items || []).find((item) => item.url === url);
    if (subItem) {
      return subItem.title;
    }

    return ""; // Should not happen
  }

  /**
   * Navigates to the specified path.
   *
   * @param {string} path - The path to navigate to
   */
  goTo(path: string): void {
    this.router.navigateByUrl(path);
  }

  /**
   * Generates keyboard shortcut string for navigation items.
   *
   * @param {number} i - The index of the navigation item
   * @returns {string} The keyboard shortcut string
   */
  getShortcut(i: number): string {
    if (i === 9) {
      return "meta.0";
    }
    return `meta.${i + 1}`;
  }

  /**
   * Toggles a URL in the favorites list. Adds if not present, removes if present.
   * Shows warning toast if maximum favorites limit is reached.
   *
   * @param {string} url - The URL to toggle in favorites
   */
  toggleFav(url: string): void {
    if (this.layoutStore.favUrls().includes(url)) {
      this.layoutStore.removeFavUrl(url);
    } else {
      if (this.layoutStore.favUrls().length < this.MAX_FAV) {
        this.layoutStore.addFavUrl(url);
      } else {
        this.toastService.add({
          severity: "warn",
          summary: this.translateService.instant("shortcuts.toast.summary"),
          detail: this.translateService.instant("shortcuts.toast.detail", { maxFav: this.MAX_FAV }),
        });
      }
    }
  }

  isAllowed(allowedRoles?: string[]): boolean {
    if (!allowedRoles || allowedRoles.length === 0) return true;

    return this.authService.hasRoles(allowedRoles);
  }

  private expandActiveSubmenu(): void {
    for (const link of this.links) {
      if (link.items?.some((item) => this.isActive(item.url))) {
        link.isCollapsed = false;
      }
    }
  }
}
